import { forwardRef, createElement, useContext } from 'react';
import { AnalyticsContext } from './AnalyticsContext';

/**
 * The wrapper forwards every prop straight through to `wrappedComponent`, so it
 * has the same props. Stating that is necessary rather than cosmetic: a
 * `forwardRef` written in a `.js` file infers no props at all, which would make
 * every prop passed to a wrapped component an error at the call site.
 *
 * `C` is bound on the inner call, not the outer one, because that is where the
 * wrapped component is supplied — binding it on `Analytics` itself would fix it
 * to its constraint before there is anything to infer it from.
 *
 * @type {(getProperties: (props: any) => Record<string, unknown>) =>
 *   <C extends React.ComponentType<any>>(wrappedComponent: C) =>
 *     React.ForwardRefExoticComponent<React.ComponentProps<C> & React.RefAttributes<unknown>>}
 */
const Analytics = (getProperties) => (wrappedComponent) => {
  const AnalyticsWrapper = forwardRef((props, ref) => {
    const contextAnalyticsData = useContext(AnalyticsContext);

    return (
      <AnalyticsContext.Provider
        value={{ ...contextAnalyticsData, ...getProperties(props) }}
      >
        {createElement(wrappedComponent, { ref, ...props })}
      </AnalyticsContext.Provider>
    );
  });

  return AnalyticsWrapper;
};

export default Analytics;
